"use client";
import Button, { ButtonProps as MuiButtonProps } from "@mui/material/Button";
import Fab, { FabProps as MuiFabProps } from "@mui/material/Fab";
import MuiLink, { LinkProps as MuiLinkProps } from "@mui/material/Link";
import NextLink from "~/components/NextLink";
import { trackEvent } from "~/connectors/analytics.connector";

import type { IconButtonProps } from "@mui/material/IconButton";
import IconButton from "@mui/material/IconButton";

type AnchorLinkProps = Omit<MuiLinkProps, "component"> & {
  effect?: "underline" | "color-on-hover" | "underline-on-hover" | "none";
};

type ButtonLinkProps = Omit<MuiButtonProps, "component" | "href"> & {
  target?: string;
  rel?: string;
};
type FabLinkProps = Omit<MuiFabProps, "component" | "href"> & {
  target?: string;
  rel?: string;
};

type IconLinkProps = Omit<IconButtonProps, "component" | "href"> & {
  target?: string;
  rel?: string;
};

type LinkProps = {
  href: string;
  trackable?: string; // Event name to track when button is clicked
  disableWordWrap?: boolean;
} & (
    | ({ button: true; fab?: never; icon?: never } & ButtonLinkProps)
    | ({ button?: never; fab: true; icon?: never } & FabLinkProps)
    | ({ button?: never; fab?: never; icon?: never } & AnchorLinkProps)
    | ({ button?: never; fab?: never; icon?: true } & IconLinkProps)
  );

function Link({
  button,
  fab,
  icon,
  children,
  href,
  trackable,
  disableWordWrap,
  onClick,
  ...rest
}: LinkProps) {
  const handleClick = (e: React.MouseEvent<any, MouseEvent>) => {
    if (trackable) {
      trackEvent(trackable, { href });
    }
    if (onClick) {
      onClick(e);
    }

    e.stopPropagation();
  };

  const isExternal =
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:");

  if (button) {
    const { ...muiButtonProps } = rest as ButtonLinkProps;
    const sx = muiButtonProps.sx || {};
    if (disableWordWrap) {
      muiButtonProps.sx = [
        ...(Array.isArray(sx) ? sx : [sx]),
        { whiteSpace: "nowrap" },
      ];
    }

    return (
      <Button
        {...muiButtonProps}
        LinkComponent={isExternal ? undefined : NextLink}
        href={href}
        onClick={handleClick}
      >
        {children}
      </Button>
    );
  }

  if (fab) {
    const { ...muiFabProps } = rest as FabLinkProps;
    if (isExternal) {
      return (
        // @ts-expect-error - MUI Fab component="a" typing has ref conflicts (HTMLButtonElement vs HTMLAnchorElement)
        // This is a known MUI typing limitation when using Fab as an anchor element for external links
        <Fab {...muiFabProps} component="a" href={href} onClick={handleClick}>
          {children}
        </Fab>
      );
    }
    return (
      <Fab
        {...muiFabProps}
        LinkComponent={NextLink}
        href={href}
        onClick={handleClick}
      >
        {children}
      </Fab>
    );
  }

  if (icon) {
    const { sx, ...iconButtonProps } = rest as IconLinkProps as any;
    const mergedSx = Array.isArray(sx) ? sx : [sx];
    return (
      <IconButton
        {...iconButtonProps}
        LinkComponent={NextLink}
        href={href}
        onClick={handleClick}
        sx={{ display: "inline-flex", ...mergedSx }}
      >
        {children}
      </IconButton>
    );
  }

  const anchorProps = rest as AnchorLinkProps;
  const { sx, effect, ...linkProps } = anchorProps;
  const muiLinkProps = linkProps as MuiLinkProps;

  return (
    <MuiLink
      component={NextLink}
      href={href}
      onClick={handleClick}
      sx={[
        disableWordWrap ? { whiteSpace: "nowrap" } : {},
        effect === "color-on-hover"
          ? {
            color: "text.secondary",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "none",
              color: "primary.main",
            },
          }
          : {},
        effect === "underline-on-hover"
          ? {
            textDecoration: "none",
            color: "primary.main",
            "&:hover": {
              textDecoration: "underline",
              color: "primary.main",
            },
          }
          : {},
        effect === "none"
          ? {
            textDecoration: "none",
            color: "inherit",
            "&:hover": {
              textDecoration: "none",
              color: "inherit",
            },
          }
          : {},
        !effect || effect === "underline"
          ? {
            textDecoration: "underline",
            color: "primary.main",
            "&:hover": {
              textDecoration: "underline",
              color: "primary.main",
            },
          }
          : {},
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...muiLinkProps}
    >
      {children}
    </MuiLink>
  );
}

export default Link;
